import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { AiSession } from "../types/ai-session.entity";
import {
  FaUser,
  FaCalendarAlt,
  FaHashtag,
  FaArrowLeft,
  FaPowerOff,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { AISessionContextHistory } from "./ai-session-context-history";
import { AISessionTransactions } from "./ai-session-transactions";
import { AISessionMessages } from "./ai-session-messages";
import { useDeactivateAISessionMutation } from "../ai-sessions.service";
import Swal from "sweetalert2";
import { Amount } from "src/components/Amount";
import {
  getAISessionContext,
  getAISessionContextDisplayLabels,
  getAISessionContextItemCount,
  getAISessionLanguage,
} from "../utils";

type Props = {
  aiSession: AiSession & { id: string };
  onBack?: () => void;
};

const badge = "inline-flex items-center px-2 py-1 rounded-full border text-xs font-medium";
const statusClass = (status: AiSession["status"]) => {
  switch (status) {
    case "ACTIVE":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "COMPLETED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    default:
      return "bg-amber-100 text-amber-700 border-amber-200";
  }
};

const contextModeClass = (mode: AiSession["context"]["sessionMode"]) => {
  switch (mode) {
    case "TOPIC":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "LEARNING_TRACK":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "WORD_BANK":
      return "bg-cyan-100 text-cyan-700 border-cyan-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const formatDate = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleDateString();
};

const formatTime = (ts: any) => {
  if (!ts) return "N/A";
  return new Date(ts?.toDate?.() || ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDuration = (minutes?: number) => {
  if (!minutes) return "N/A";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const formatScore = (score?: AiSession["score"]) => {
  if (!score) return "N/A";
  return `${score.overall.toFixed(1)}%`;
};

export const AISessionDetails: React.FC<Props> = ({ aiSession, onBack }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const preferencesRef = useRef<HTMLDivElement>(null);
  const [deactivateAISession, { isLoading: isDeactivating }] = useDeactivateAISessionMutation();
  const sessionContext = useMemo(() => getAISessionContext(aiSession), [aiSession]);
  const sessionContextLabels = useMemo(() => getAISessionContextDisplayLabels(aiSession), [aiSession]);
  const sessionContextItemCount = useMemo(() => getAISessionContextItemCount(aiSession), [aiSession]);
  const sessionLanguage = useMemo(() => getAISessionLanguage(aiSession), [aiSession]);
  const sessionContextMode = sessionContext.sessionMode;

  // Check if deactivate button should be shown - memoized to prevent re-renders
  const shouldShowDeactivateButton = useMemo(() => {
    if (aiSession.status !== "ACTIVE") return false;
    
    const now = new Date();
    const startTime = aiSession.startTime?.toDate?.() || 
      (aiSession.startTime ? new Date(aiSession.startTime.seconds * 1000) : new Date());
    const timeDiffMinutes = (now.getTime() - startTime.getTime()) / (1000 * 60);
    
    return timeDiffMinutes > 35;
  }, [aiSession.status, aiSession.startTime]);

  const handleDeactivate = useCallback(async () => {
    try {
      const result = await Swal.fire({
        title: 'Deactivate AI Session?',
        text: 'Are you sure you want to deactivate this AI session? This action cannot be undone.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Deactivate',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        // Show loading state
        Swal.fire({
          title: 'Deactivating...',
          text: 'Please wait while we deactivate the AI session...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await deactivateAISession(aiSession.id).unwrap();
        
        // Close loading and show success
        Swal.close();
        Swal.fire({
          title: 'Success! 🎉',
          text: 'AI session deactivated successfully',
          icon: 'success',
          confirmButtonColor: '#10b981'
        });
      }
    } catch (error) {
      // Close loading and show error
      Swal.close();
      Swal.fire({
        title: 'Error! ❌',
        text: 'Failed to deactivate AI session. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444'
      });
    }
  }, [deactivateAISession, aiSession.id]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShowPreferences(true);
          }
        });
      },
      { threshold: 0.15 }
    );

    if (preferencesRef.current) observer.observe(preferencesRef.current);

    return () => observer.disconnect();
  }, []);

  const baseItemClasses = "transform transition-all duration-300 ease-out";
  const hiddenItemClasses = "opacity-0 translate-y-1";
  const visibleItemClasses = "opacity-100 translate-y-0";
  const cx = (extra: string = "") =>
    `${baseItemClasses} ${isVisible ? visibleItemClasses : hiddenItemClasses} ${extra}`.trim();
  const delayStyle = (ms: number) => ({ transitionDelay: `${ms}ms` } as React.CSSProperties);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        {/* Animation styles provided by animate.css (animate__zoomIn) */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">AI Session Details</h1>
          <div className="flex items-center gap-3">
            {shouldShowDeactivateButton && (
              <button
                onClick={handleDeactivate}
                disabled={isDeactivating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaPowerOff />
                {isDeactivating ? 'Deactivating...' : 'Deactivate'}
              </button>
            )}
            {onBack && (
              <button
                onClick={onBack}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-1"
              >
                <FaArrowLeft />
                Back
              </button>
            )}
          </div>
        </div>

        <div
          className={
            "bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transform transition-all duration-300 ease-out " +
            (isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95")
          }
        >
          <div className="p-6 border-b border-gray-100 flex items-center gap-3 flex-wrap">
            <span className={cx(`${badge} ${statusClass(aiSession.status)}`)} style={delayStyle(60)}>
              {aiSession.status}
            </span>
            <span className={cx(`${badge} ${contextModeClass(sessionContextMode)}`)} style={delayStyle(120)}>
              Mode: {sessionContextMode}
            </span>
            <span className={cx(`${badge} bg-slate-100 text-slate-700 border-slate-200`)} style={delayStyle(150)}>
              Context Items: {sessionContextItemCount}
            </span>
            <span className={cx(`${badge} bg-cyan-100 text-cyan-700 border-cyan-200`)} style={delayStyle(180)}>
              Language: {sessionLanguage ?? "None"}
            </span>
            {aiSession.score && (
              <span className={cx(`${badge} bg-green-100 text-green-700 border-green-200`)} style={delayStyle(210)}>
                Score: {formatScore(aiSession.score)}
              </span>
            )}
            <span className={cx(`${badge} ${aiSession.isAdministrated ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`)} style={delayStyle(270)}>
              {aiSession.isAdministrated ? <><FaCheckCircle className="mr-1" /> Administered</> : <><FaTimesCircle className="mr-1" /> Not Administered</>}
            </span>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className={cx("flex items-start gap-3")} style={delayStyle(80)}>
              <FaHashtag className="mt-1 text-gray-500" />
              <div>
                <div className="text-sm text-gray-500">Session ID</div>
                <div className="font-mono text-sm">{aiSession.id}</div>
              </div>
            </div>
            <div className={cx("flex items-center gap-3")} style={delayStyle(120)}>
              <FaUser className="text-blue-500" />
              <span className="font-semibold">User:</span>
              <Link
                to={`/users/${aiSession.userId}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 no-underline font-mono font-semibold text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
              >
                {aiSession.userId}
              </Link>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(160)}>
              <FaHashtag className="text-purple-500" />
              <span className="font-semibold">Context Mode:</span>
              <div className="flex flex-wrap gap-1">
                <span className={`px-2 py-1 rounded text-xs border ${contextModeClass(sessionContextMode)}`}>
                  {sessionContextMode}
                </span>
              </div>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(180)}>
              <FaHashtag className="text-slate-500" />
              <span className="font-semibold">Context:</span>
              <div className="flex flex-wrap gap-1">
                {sessionContextLabels.length > 0 ? (
                  sessionContextLabels.map((label: string, idx: number) => (
                    <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                      {label}
                    </span>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">
                    {sessionContextItemCount > 0
                      ? `${sessionContextItemCount} item${sessionContextItemCount === 1 ? "" : "s"}`
                      : "None"}
                  </span>
                )}
              </div>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(200)}>
              <FaCalendarAlt className="text-emerald-600" />
              <span className="font-semibold">Duration:</span>
              <span className="font-semibold text-emerald-700">{formatDuration(aiSession.durationInMins ?? undefined)}</span>
            </div>

            <div className={cx("flex items-center gap-3")} style={delayStyle(220)}>
              <FaHashtag className="text-cyan-500" />
              <span className="font-semibold">Language:</span>
              <div className="flex flex-wrap gap-1">
                {sessionLanguage ? (
                  <span className="px-2 py-1 bg-cyan-100 text-cyan-700 rounded text-xs">
                    {sessionLanguage}
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">None</span>
                )}
              </div>
            </div>

            {aiSession.score && (
              <div className={cx("flex items-center gap-3")} style={delayStyle(240)}>
                <FaCheckCircle className="text-green-600" />
                <span className="font-semibold">Score:</span>
                <span className="font-semibold text-green-700">{formatScore(aiSession.score)}</span>
                {aiSession.score.grammar && (
                  <span className="text-xs text-gray-500 ml-2">
                    (G: {aiSession.score.grammar.toFixed(1)}%, E: {aiSession.score.engagement.toFixed(1)}%, F: {aiSession.score.fluency.toFixed(1)}%)
                  </span>
                )}
              </div>
            )}

            {aiSession.deductedMoney && (
              <div className={cx("flex items-center gap-3")} style={delayStyle(280)}>
                <FaCalendarAlt className="text-amber-600" />
                <span className="font-semibold">Amount Deducted:</span>
                <span className="font-semibold text-amber-700">
                  <Amount amount={aiSession.deductedMoney.amount} currency={aiSession.deductedMoney.currency} />
                </span>
              </div>
            )}

            <div className={cx("flex items-center gap-3")} style={delayStyle(320)}>
              <FaCalendarAlt className="text-teal-600" />
              <span className="font-semibold">Started:</span>
              <span className="leading-tight">
                <div>{formatDate(aiSession.startTime)}</div>
                <div>{formatTime(aiSession.startTime)}</div>
              </span>
            </div>

            {aiSession.endTime && (
              <div className={cx("flex items-center gap-3")} style={delayStyle(360)}>
                <FaCalendarAlt className="text-red-600" />
                <span className="font-semibold">Ended:</span>
                <span className="leading-tight">
                  <div>{formatDate(aiSession.endTime)}</div>
                  <div>{formatTime(aiSession.endTime)}</div>
                </span>
              </div>
            )}

          </div>
        </div>

        {/* Score Details */}
        {aiSession.score && (
          <div
            ref={preferencesRef}
            className={`mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden ${
              showPreferences ? "animate__animated animate__backInRight" : "opacity-0"
            }`}
          >
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold m-0">
                <span className="inline-flex items-center px-4 py-1.5 rounded-full border text-sm font-semibold bg-green-50 text-green-700 border-green-200">
                  Score Details
                </span>
              </h2>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="text-sm text-slate-500">Overall Score</div>
                <div className="font-semibold text-lg text-green-700">{aiSession.score.overall.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Grammar</div>
                <div className="font-semibold">{aiSession.score.grammar.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Engagement</div>
                <div className="font-semibold">{aiSession.score.engagement.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Fluency</div>
                <div className="font-semibold">{aiSession.score.fluency.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Vocabulary</div>
                <div className="font-semibold">{aiSession.score.vocabulary.toFixed(1)}%</div>
              </div>
              <div>
                <div className="text-sm text-slate-500">Relevancy</div>
                <div className="font-semibold">{aiSession.score.relevancy.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}

        <AISessionContextHistory aiSession={aiSession} />

        {/* Transactions Section */}
        <div className="mt-8">
          <AISessionTransactions sessionId={aiSession.id} />
        </div>

        <AISessionMessages sessionId={aiSession.id} />
      </div>
    </div>
  );
};

export default AISessionDetails;
